// Rich-content rendering for the widget.
//
// A `rich_content` IVA tool sends a `render.richContent` directive. Two render
// modes are supported:
//   - adaptive_card: a lightweight subset Adaptive Card renderer (TextBlock,
//     Image, RichTextBlock, FactSet, Container, ColumnSet, ImageSet, ActionSet,
//     Input.Text/Number/Toggle/Date/ChoiceSet, Action.Submit). Keeps the embed
//     bundle dependency-free.
//   - custom_html: arbitrary HTML/JS rendered inside a sandboxed iframe
//     (sandbox="allow-scripts"), with a postMessage bridge that injects
//     params/data and relays form submissions back out. This covers graphs,
//     carousels, and any rich visualisation the card schema can't express.

export interface RichContentDirective {
  type: 'render.richContent';
  toolCallId: string;
  toolName: string;
  renderMode: 'adaptive_card' | 'custom_html';
  template: string;
  interaction: 'display_only' | 'await_submit';
  awaitsSubmit?: boolean;
  data?: Record<string, any>;
  params?: Record<string, any>;
}

type SubmitFn = (toolCallId: string, formData: Record<string, any>) => void;
/** Build a DOM node for a rich-content directive. */
export function renderRichContent(directive: RichContentDirective, onSubmit: SubmitFn): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'rich-content';
  wrap.setAttribute('data-tool', directive.toolName || '');
  try {
    if (directive.renderMode === 'custom_html') {
      wrap.appendChild(renderCustomHtml(directive, onSubmit));
    } else {
      wrap.appendChild(renderAdaptiveCard(directive, onSubmit));
    }
  } catch (err: any) {
    const fallback = document.createElement('div');
    fallback.className = 'rich-content-error';
    fallback.textContent = 'Unable to render content.';
    wrap.appendChild(fallback);
  }
  return wrap;
}

// ── custom_html (sandboxed iframe) ──────────────────────────────────────────

function renderCustomHtml(directive: RichContentDirective, onSubmit: SubmitFn): HTMLElement {
  const channel = `rc-${directive.toolCallId}-${Math.random().toString(36).slice(2, 8)}`;
  const iframe = document.createElement('iframe');
  iframe.className = 'rich-content-frame';
  // allow-scripts only — no allow-same-origin, so the frame is fully isolated
  // from the host (cannot read cookies/storage or reach back into the DOM).
  iframe.setAttribute('sandbox', 'allow-scripts');
  iframe.style.width = '100%';
  iframe.style.border = '0';
  iframe.style.minHeight = '120px';

  const payload = {
    channel,
    params: directive.params || {},
    data: directive.data || {},
    interaction: directive.interaction,
  };

  // The bridge script: exposes window.autoqm.{params,data,submit,resize} to the
  // author's HTML, and posts submit/resize messages tagged with the channel.
  const bridge = `
<script>
(function(){
  var CH = ${JSON.stringify(channel)};
  var ctx = ${JSON.stringify(payload)};
  window.autoqm = {
    params: ctx.params,
    data: ctx.data,
    interaction: ctx.interaction,
    submit: function(formData){
      parent.postMessage({ source: 'autoqm-rc', channel: CH, kind: 'submit', formData: formData || {} }, '*');
    },
    resize: function(h){
      parent.postMessage({ source: 'autoqm-rc', channel: CH, kind: 'resize', height: h }, '*');
    }
  };
  // Auto-wire any <form> so a plain HTML form posts via the bridge.
  document.addEventListener('submit', function(e){
    var f = e.target;
    if (!f || f.tagName !== 'FORM') return;
    e.preventDefault();
    var out = {};
    try {
      var fd = new FormData(f);
      fd.forEach(function(v, k){ out[k] = v; });
    } catch (_){}
    window.autoqm.submit(out);
  }, true);
  // Auto-resize to content on load.
  function reportSize(){
    try { window.autoqm.resize(document.documentElement.scrollHeight); } catch(_){}
  }
  window.addEventListener('load', reportSize);
  setTimeout(reportSize, 200);
})();
<\/script>`;

  // NOTE: the bridge MUST come before the author's template so that
  // window.autoqm (with params/data/submit) is defined before any inline
  // <script> in the template runs during parsing. Otherwise scripts that read
  // window.autoqm.data (e.g. to iterate an array) see it undefined and render
  // empty, while {{data.x}} scalar placeholders (substituted server-side) still
  // appear — exactly the "renders scalars but not the list" symptom.
  const html = `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<base target="_blank">` +
    `<style>body{margin:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;color:#1f2937;padding:8px}</style>` +
    bridge +
    `</head><body>${directive.template || ''}</body></html>`;
  iframe.srcdoc = html;

  // Listen for messages from THIS frame's channel only.
  const onMessage = (ev: MessageEvent): void => {
    const d = ev.data;
    if (!d || d.source !== 'autoqm-rc' || d.channel !== channel) return;
    if (d.kind === 'resize' && typeof d.height === 'number') {
      iframe.style.height = `${Math.max(80, Math.min(2000, d.height))}px`;
    } else if (d.kind === 'submit') {
      onSubmit(directive.toolCallId, d.formData || {});
      // After a submit, lock the frame to prevent duplicate submissions.
      if (directive.interaction === 'await_submit') {
        window.removeEventListener('message', onMessage);
      }
    }
  };
  window.addEventListener('message', onMessage);
  return iframe;
}

// ── adaptive_card (lightweight subset) ──────────────────────────────────────

function renderAdaptiveCard(directive: RichContentDirective, onSubmit: SubmitFn): HTMLElement {
  let card: any;
  try {
    card = typeof directive.template === 'string' ? JSON.parse(directive.template) : directive.template;
  } catch {
    const pre = document.createElement('div');
    pre.className = 'rich-content-text';
    pre.textContent = directive.template || '';
    return pre;
  }

  const root = document.createElement('div');
  root.className = 'ac-card';
  const inputs: Record<string, () => any> = {};

  const body = Array.isArray(card?.body) ? card.body : [];
  for (const item of body) root.appendChild(renderAcElement(item, inputs));

  const actions = Array.isArray(card?.actions) ? card.actions : [];
  if (actions.length) {
    const actionsEl = document.createElement('div');
    actionsEl.className = 'ac-actions';
    for (const action of actions) {
      const btn = renderAcAction(action, inputs, directive, onSubmit);
      if (btn) actionsEl.appendChild(btn);
    }
    root.appendChild(actionsEl);
  }
  return root;
}

function collectInputs(inputs: Record<string, () => any>, extra?: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...(extra || {}) };
  for (const [id, getter] of Object.entries(inputs)) {
    try { out[id] = getter(); } catch { /* */ }
  }
  return out;
}

function renderAcAction(
  action: any,
  inputs: Record<string, () => any>,
  directive: RichContentDirective,
  onSubmit: SubmitFn,
): HTMLElement | null {
  const type = action?.type || '';
  if (type === 'Action.OpenUrl' && action.url) {
    const a = document.createElement('a');
    a.className = 'ac-btn ac-btn-link';
    a.textContent = action.title || 'Open';
    a.href = String(action.url);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    return a;
  }
  if (type === 'Action.Submit') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ac-btn ac-btn-primary';
    btn.textContent = action.title || 'Submit';
    btn.addEventListener('click', () => {
      const payload = collectInputs(inputs, action.data && typeof action.data === 'object' ? action.data : undefined);
      onSubmit(directive.toolCallId, payload);
      if (directive.interaction === 'await_submit') {
        // Disable the card's actions after submission.
        btn.disabled = true;
        btn.textContent = action.title || 'Submitted';
      }
    });
    return btn;
  }
  return null;
}

function renderAcElement(item: any, inputs: Record<string, () => any>): HTMLElement | Comment {
  const type = item?.type || '';
  switch (type) {
    case 'TextBlock': {
      const t = document.createElement('div');
      t.className = 'ac-text';
      if (item.weight === 'Bolder') t.style.fontWeight = '600';
      if (item.size === 'Large') t.style.fontSize = '18px';
      else if (item.size === 'Medium') t.style.fontSize = '16px';
      else if (item.size === 'Small') t.style.fontSize = '12px';
      if (item.color === 'Good') t.style.color = '#15803d';
      else if (item.color === 'Attention') t.style.color = '#b91c1c';
      else if (item.color === 'Warning') t.style.color = '#b45309';
      if (item.wrap === false) t.style.whiteSpace = 'nowrap';
      if (item.horizontalAlignment) t.style.textAlign = String(item.horizontalAlignment).toLowerCase();
      t.textContent = String(item.text ?? '');
      return t;
    }
    case 'Image': {
      const url = String(item.url || '').trim();
      // Skip images with no resolved URL (e.g. unbound {{data.imageUrl}}) instead
      // of rendering a broken image / alt-text placeholder.
      if (!url) return document.createComment('image:no-src');
      const img = document.createElement('img');
      img.className = 'ac-image';
      img.src = url;
      img.alt = String(item.altText || '');
      img.style.maxWidth = '100%';
      if (item.size === 'Small') img.style.width = '40px';
      else if (item.size === 'Medium') img.style.width = '80px';
      else if (item.size === 'Large') img.style.width = '160px';
      if (item.horizontalAlignment === 'Center') { img.style.display = 'block'; img.style.margin = '0 auto'; }
      return img;
    }
    case 'ImageSet': {
      const set = document.createElement('div');
      set.className = 'ac-imageset';
      for (const im of item.images || []) set.appendChild(renderAcElement({ ...im, type: 'Image' }, inputs));
      return set;
    }
    case 'FactSet': {
      const table = document.createElement('div');
      table.className = 'ac-factset';
      for (const f of item.facts || []) {
        const row = document.createElement('div');
        row.className = 'ac-fact';
        const k = document.createElement('span');
        k.className = 'ac-fact-title';
        k.textContent = String(f.title ?? '');
        const v = document.createElement('span');
        v.className = 'ac-fact-value';
        v.textContent = String(f.value ?? '');
        row.appendChild(k); row.appendChild(v);
        table.appendChild(row);
      }
      return table;
    }
    case 'Container': {
      const c = document.createElement('div');
      c.className = 'ac-container';
      for (const child of item.items || []) c.appendChild(renderAcElement(child, inputs));
      return c;
    }
    case 'ColumnSet': {
      const cs = document.createElement('div');
      cs.className = 'ac-columnset';
      cs.style.display = 'flex';
      cs.style.gap = '8px';
      for (const col of item.columns || []) {
        const colEl = document.createElement('div');
        colEl.className = 'ac-column';
        colEl.style.flex = col.width && col.width !== 'auto' && col.width !== 'stretch' ? String(col.width) : '1';
        for (const child of col.items || []) colEl.appendChild(renderAcElement(child, inputs));
        cs.appendChild(colEl);
      }
      return cs;
    }
    case 'Input.Text': {
      const field = labeledField(item.label || item.placeholder);
      const input = item.isMultiline
        ? document.createElement('textarea')
        : document.createElement('input');
      input.className = 'ac-input';
      if (item.placeholder) input.setAttribute('placeholder', String(item.placeholder));
      if (item.value != null) (input as any).value = String(item.value);
      if (!item.isMultiline && item.style === 'Email') (input as HTMLInputElement).type = 'email';
      else if (!item.isMultiline && item.style === 'Tel') (input as HTMLInputElement).type = 'tel';
      const id = String(item.id || '');
      if (id) inputs[id] = () => (input as any).value;
      field.appendChild(input);
      return field;
    }
    case 'Input.Number': {
      const field = labeledField(item.label || item.placeholder);
      const input = document.createElement('input');
      input.className = 'ac-input';
      input.type = 'number';
      if (item.placeholder) input.placeholder = String(item.placeholder);
      if (item.value != null) input.value = String(item.value);
      if (item.min != null) input.min = String(item.min);
      if (item.max != null) input.max = String(item.max);
      const id = String(item.id || '');
      if (id) inputs[id] = () => (input.value === '' ? null : Number(input.value));
      field.appendChild(input);
      return field;
    }
    case 'Input.Date': {
      const field = labeledField(item.label);
      const input = document.createElement('input');
      input.className = 'ac-input';
      input.type = 'date';
      if (item.value != null) input.value = String(item.value);
      const id = String(item.id || '');
      if (id) inputs[id] = () => input.value;
      field.appendChild(input);
      return field;
    }
    case 'Input.Toggle': {
      const field = document.createElement('label');
      field.className = 'ac-toggle';
      const input = document.createElement('input');
      input.type = 'checkbox';
      const onVal = item.valueOn != null ? String(item.valueOn) : 'true';
      const offVal = item.valueOff != null ? String(item.valueOff) : 'false';
      if (item.value != null && String(item.value) === onVal) input.checked = true;
      const span = document.createElement('span');
      span.textContent = String(item.title || item.label || '');
      field.appendChild(input);
      field.appendChild(span);
      const id = String(item.id || '');
      if (id) inputs[id] = () => (input.checked ? onVal : offVal);
      return field;
    }
    case 'Input.ChoiceSet': {
      const field = labeledField(item.label);
      const choices = Array.isArray(item.choices) ? item.choices : [];
      if (item.style === 'expanded' && !item.isMultiSelect) {
        const group = document.createElement('div');
        group.className = 'ac-choices';
        const name = `c-${item.id || Math.random().toString(36).slice(2)}`;
        for (const ch of choices) {
          const lbl = document.createElement('label');
          lbl.className = 'ac-choice';
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = name;
          radio.value = String(ch.value);
          if (item.value != null && String(item.value) === String(ch.value)) radio.checked = true;
          lbl.appendChild(radio);
          lbl.appendChild(document.createTextNode(String(ch.title ?? ch.value)));
          group.appendChild(lbl);
        }
        const id = String(item.id || '');
        if (id) inputs[id] = () => {
          const checked = group.querySelector('input:checked') as HTMLInputElement | null;
          return checked ? checked.value : null;
        };
        field.appendChild(group);
      } else {
        const select = document.createElement('select');
        select.className = 'ac-input';
        if (item.isMultiSelect) select.multiple = true;
        for (const ch of choices) {
          const opt = document.createElement('option');
          opt.value = String(ch.value);
          opt.textContent = String(ch.title ?? ch.value);
          if (item.value != null && String(item.value) === String(ch.value)) opt.selected = true;
          select.appendChild(opt);
        }
        const id = String(item.id || '');
        if (id) inputs[id] = () => item.isMultiSelect
          ? Array.from(select.selectedOptions).map(o => o.value)
          : select.value;
        field.appendChild(select);
      }
      return field;
    }
    default: {
      // Unknown element — render any text we can find, else skip silently.
      const d = document.createElement('div');
      if (item?.text) { d.className = 'ac-text'; d.textContent = String(item.text); }
      return d;
    }
  }
}

function labeledField(label?: any): HTMLElement {
  const field = document.createElement('div');
  field.className = 'ac-field';
  if (label) {
    const l = document.createElement('label');
    l.className = 'ac-label';
    l.textContent = String(label);
    field.appendChild(l);
  }
  return field;
}
